import React from 'react';
import {action, autorun, computed, observable, runInAction} from 'mobx';
import {DEFAULT_USER_GUIDE} from "../../../constants/joyride";
import {getRegisteredAt, getUserProperties, getUserReady, isSchoolUser, setUserProperties} from "../../../utils/userUtility";
import {translate} from '../../../utils/localizationUtility';
import {ACTIONS, EVENTS, STATUS} from 'react-joyride';
import {getStorage} from "../../../utils/storageHelper";
import {customEvent} from "../../../utils/analyticsUtil";
import {actions, category, events} from "../../../constants/enums";
import mainStore from "../../../containers/main/store";
import projectStore from "../../../containers/main/project/store";


class GuideStore {
    @observable guideList = [];
    @observable guide = {skipNext: false, stepIndex: 0, steps: [], skipped: false};
    // @observable stepIndex = 0;
    // @observable skipNext = false;

    @observable locale = {
        back: 'Back',
        close: 'Close',
        last: 'Done',
        next: 'Next',
        skip: 'Skip'
    };

    constructor() {
        setTimeout(() => {
            autorun(() => {
                if (getUserReady()) {
                    this.initLocale();
                    this.initGuideList();
                }
            });
        })

        autorun(() => {
            if (mainStore?.deviceOrientation !== this.deviceOrientation) {
                this.deviceOrientation = mainStore.deviceOrientation;
                if (this.guide && this.guide.runGuide) {
                    runInAction(() => {
                        this.guide.runGuide = false;
                    });
                    setImmediate(() => {
                        runInAction(() => {
                            if (this.guide) {
                                this.guide.runGuide = true;
                            }
                        });
                    })
                }
            }
        })
    }

    @computed
    get isDuringTimelineFirstGuide() {
        return this.guide && this.guide.label === 'Timeline Intro' && !this.guide.skipped;
    }

    @computed
    get isDuringTimelineFirstGuideAndRunning() {
        return this.guide && this.guide.label === 'Timeline Intro' && this.guide.runGuide;
    }


    @action initLocale = () => {
        this.locale = {
            back: translate('joyride.back'),
            close: translate('joyride.close'),
            last: translate('joyride.done'),
            next: translate('joyride.next'),
            skip: translate('joyride.skip')
        };
    };


    @action initGuideList = () => {
        try {
            let userGuideList = getUserProperties().guide || DEFAULT_USER_GUIDE;
            let override = getStorage().getItem('test-musico-joyride') === 'true'

            // https://musicoio.atlassian.net/browse/MM-6912 - disable guides
            let disableAllGuides = true;
            try {
                if (isSchoolUser() || getRegisteredAt() < new Date('2020-08-04')) {
                    disableAllGuides = true;
                }
            } catch (err) {
            }
            this.guideList = DEFAULT_USER_GUIDE.map(defGuide => {
                let guideData = userGuideList.find(guide =>
                    guide.routeKey === defGuide.routeKey
                    && guide.routeParams === defGuide.routeParams
                    && !guide.alreadyFound
                );
                if (guideData) {
                    guideData.alreadyFound = true;
                }
                guideData = Object.assign({}, defGuide, guideData);
                try {
                    if (getStorage().getItem('test-musico-joyride') === 'true') {
                        guideData.runGuide = guideData.runGuide || override;
                    }
                    if (disableAllGuides) {
                        guideData.runGuide = override;
                    }
                } catch (err) {
                }
                return guideData;
            });
        } catch (err) {
            console.log(err.message);
        }
    };

    @action fixNextTarget() {
        try {
            if (typeof this.guide.steps[this.guide.stepIndex].target === 'function') {
                this.guide.steps[this.guide.stepIndex].target = (this.guide.steps[this.guide.stepIndex].target)(projectStore.activities.length > 0);
            }
        } catch (err) {
        }
    }

    @action unsetCurrentGuide = () => {
        this.guide = {skipNext: false, stepIndex: 0, steps: [], skipped: false};
    }

    @action setCurrentGuide = (key, params) => {
        // console.log(key, params);
        this.guide = this.getGuideByKey(key, params);
        // force guide refresh
        if (this.guide) {
            const runGuide = this.guide.runGuide;
            // console.log('setting guide: ', this.guide.label, runGuide);
            if (runGuide) {
                this.sendGuideEvent(actions.ShowGuide);
                this.fixNextTarget();
            }
            this.guide.runGuide = false;
            setImmediate(() => {
                runInAction(() => {
                    this.guide.runGuide = runGuide;
                    // console.log('setting guide: ', this.guide.label, this.guide.runGuide);
                })
            })
        }
        // console.log('setting guide: ' + key,' ', this.guide);
    };

    getGuideByKey = (key, params) => {
        let guide = this.guideList.find(item => item.routeKey === key && item.routeParams === params && item.runGuide)
        return guide;
    }


    @action hideGuide = () => {
        if (this.guide) {
            // console.log('hiding guide ', this.guide.label, this.guide.runGuide)
            const wasGuideRunning = this.guide.runGuide;
            this.guide.disableBeacon = false;
            this.guide.runGuide = false;
            this.guide.stepIndex = 0;
            // only update if guide was running and now it's not
            if (wasGuideRunning) {
                // console.log('saving storage for guide: ', this.guide.label)
                this.saveToLocalStorage();
            }
        }
    };

    isStepRunning = (label, stepLabel) => {
        return this.guide && this.guide.runGuide && this.guide.label === label && this.guide.steps[this.guide.stepIndex]
            && this.guide.steps[this.guide.stepIndex].label === stepLabel;
    }


    saveToLocalStorage = () => {
        // console.log(this.guideList.map(guide => {return guide.label + ' ' + guide.runGuide;}));

        setUserProperties({
            guide: this.guideList.map(guide => {
                return {routeKey: guide.routeKey, routeParams: guide.routeParams, runGuide: guide.runGuide}
            })
        });
    };

    sendGuideEvent(action) {
        const guide = this.guide || {steps: [{}], stepIndex: 0};
        const guideLabel = guide.label;
        const stepLabel = guide.steps[guide.stepIndex].label;
        customEvent(events.CustomEvent, {category: category.Joyride, action: action, label: guideLabel + ' ' + stepLabel});
    }


    @action handleJoyrideCallback = (data) => {
        const {action, index, status, type} = data;
        if (!this.guide) {
            return;
        }

        if (action === ACTIONS.CLOSE || [STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
            // Need to set our running state to false, so we can restart if we click start again.
            this.hideGuide();
            this.sendGuideEvent(status === STATUS.SKIPPED ? actions.SkipGuide : actions.DoneGuide)
            if (status === STATUS.SKIPPED) {
                this.guide.skipped = true;
            }
        } else if (index === this.guide.stepIndex && [EVENTS.TARGET_NOT_FOUND].includes(type)) {
            // this.guide.stepIndex -= dir;
            this.sendGuideEvent(actions.NotFoundStepGuide);
            console.log('target not found setting runGuide to false', this.guide.label);
            this.guide.runGuide = false;
            if (this.guide.times > 3) {
                return;
            }
            setTimeout(() => {
                runInAction(() => {
                    // console.log('target not found setting runGuide to true', this.guide.label);
                    if (!this.guide.times) {
                        this.guide.times = 1;
                    } else {
                        this.guide.times++;
                    }
                    this.guide.runGuide = true;
                })
            }, 500);
            return;
        } else if (index === this.guide.stepIndex && [EVENTS.STEP_AFTER].includes(type)) {
            const dir = (action === ACTIONS.PREV ? -1 : 1);
            // Update state to advance the tour
            this.sendGuideEvent(dir > 0 ? actions.NextGuide : actions.BackGuide);
            this.guide.stepIndex += dir;
        }
        this.guide.stepIndex = Math.min(this.guide.steps.length, Math.max(0, this.guide.stepIndex));
        this.fixNextTarget();
    };

    @action next = () => {
        if (!this.guide || !this.guide.runGuide || this.guide.stepIndex >= this.guide.steps.length) {
            // there's no active guide, ignore the request
            return;
        }
        this.sendGuideEvent(actions.NextGuide);

        this.guide.stepIndex++;
        this.fixNextTarget();
    }


}

const guideStore = new GuideStore();
export default guideStore;
